import type { DrawerSize, DrawerWidth } from './types'

export const DRAWER_WIDTH_BY_SIZE: Record<DrawerSize, DrawerWidth> = {
  sm: '24rem',
  md: '32rem',
  lg: '42rem',
  xl: '56rem',
}

function splitArguments(value: string): [string, string] | null {
  let depth = 0
  for (let index = 0; index < value.length; index += 1) {
    const character = value[index]
    if (character === '(') depth += 1
    else if (character === ')') depth -= 1
    else if (character === ',' && depth === 0) {
      return [value.slice(0, index).trim(), value.slice(index + 1).trim()]
    }
  }
  return null
}

export function resolveDrawerWidth(
  value: DrawerWidth,
  viewportWidth: number,
  rootFontSize = 16,
): number {
  if (typeof value === 'number') return value

  const input = value.trim().toLowerCase()
  const numeric = Number.parseFloat(input)
  if (/^-?\d+(?:\.\d+)?px$/.test(input)) return numeric
  if (/^-?\d+(?:\.\d+)?rem$/.test(input)) return numeric * rootFontSize
  if (/^-?\d+(?:\.\d+)?vw$/.test(input)) return (numeric / 100) * viewportWidth

  const calculation = input.match(/^calc\(100vw\s*-\s*(-?\d+(?:\.\d+)?)(px|rem)\)$/)
  if (calculation) {
    const amount = Number.parseFloat(calculation[1] ?? '0')
    const subtraction = calculation[2] === 'rem' ? amount * rootFontSize : amount
    return viewportWidth - subtraction
  }

  const functionCall = input.match(/^(min|max)\((.*)\)$/)
  if (functionCall) {
    const argumentsPair = splitArguments(functionCall[2] ?? '')
    if (argumentsPair) {
      const values = argumentsPair.map((argument) =>
        resolveDrawerWidth(argument, viewportWidth, rootFontSize),
      )
      return functionCall[1] === 'min' ? Math.min(...values) : Math.max(...values)
    }
  }

  throw new TypeError(`Unsupported drawer width expression: ${value}`)
}
